/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAppDispatch } from "../../../store/rootStore";
import { useSeedBranch } from "../../../util/useBranch";
import { selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import { customName, premixName } from "../components/NewSeedTypeButton";
import { SEED_SUMMARY_ROUTE } from "../constants";
import { SEED_MIX_TYPE, SeedMix, SeedMixDefaultValue } from "../types/SeedOrderFormType";

import { insertSeedMixAfter, selectSeedOrder } from "./seedCart.slice";
import { syncSeedCart } from "./seedCartThunk";
import { SeedErrorCode } from "./types";

function incrementStringNumber(input: string): string {
  // Use a regular expression to match a number at the end of the string
  const regex = /(\d+)$/;

  const match = regex.exec(input);

  if (match) {
    // If a number is found, increment it
    const number = parseInt(match[0], 10);
    const incrementedNumber = number + 1;
    // Replace the old number with the incremented one
    return input.replace(regex, incrementedNumber.toString());
  } else {
    // If no number is found, append " 1"
    return `${input} 1`;
  }
}

export const useSeedCart = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const account = useSelector(selectCurrentFarmerAccount);
  const accountNumber = account?.accountNumber ?? "";

  const seedOrders = useSelector(selectSeedOrder(accountNumber));

  const branchId = useSeedBranch();

  const copySeed = (seed?: SeedMix) => {
    if (!accountNumber) {
      return;
    }

    const newSeed: SeedMix = JSON.parse(JSON.stringify(seed));
    newSeed.id = null;
    newSeed.products.forEach((product) => {
      product.lineId = null;
    });

    const count = seedOrders?.mixes.length ?? 0;

    let name = premixName + " " + (count + 1).toString();

    if (seed?.name) {
      name = incrementStringNumber(seed.name);
    }

    const finalSeed = {
      ...SeedMixDefaultValue,
      ...newSeed,
      name: name,
    } as SeedMix;

    if (!finalSeed.deliveryAddress && !finalSeed.storeId) {
      finalSeed.storeId = branchId;
    }

    dispatch(
      insertSeedMixAfter({
        accountNumber: accountNumber,
        seedMixA: seed,
        seedMixB: finalSeed,
      })
    );
  };

  const addCustomSeed = () => {
    if (!accountNumber) {
      return;
    }

    const names = seedOrders?.mixes
      .map((e) => e.name)
      .filter((e) => e.startsWith("Custom seed mix"));
    const count = names?.length ?? 0;

    let name = `${customName} ${count + 1}`;
    let uniqueName = name;

    // Loop to check for existing names and update the count until a unique name is found
    let counter = count + 1;
    while (names?.includes(uniqueName)) {
      counter++;
      uniqueName = `${premixName} ${counter}`;
    }

    name = uniqueName;

    const seedOrder = {
      ...SeedMixDefaultValue,
      mixType: SEED_MIX_TYPE.CUSTOM,
      name: name,
      storeId: branchId,
    };

    dispatch(
      insertSeedMixAfter({
        accountNumber: accountNumber,
        seedMixB: seedOrder,
      })
    );
  };

  const addPremixSeed = async () => {
    if (!accountNumber) {
      return;
    }

    const names = seedOrders?.mixes
      .map((e) => e.name)
      .filter((e) => e.startsWith("Straight or premixed"));
    const count = names?.length ?? 0;

    let name = `${premixName} ${count + 1}`;
    let uniqueName = name;

    // Loop to check for existing names and update the count until a unique name is found
    let counter = count + 1;
    while (names?.includes(uniqueName)) {
      counter++;
      uniqueName = `${premixName} ${counter}`;
    }

    name = uniqueName;

    const seedOrder = {
      ...SeedMixDefaultValue,
      mixType: SEED_MIX_TYPE.PREMIX,
      name: name,
      storeId: branchId,
    };

    dispatch(
      insertSeedMixAfter({
        accountNumber: accountNumber,
        seedMixB: seedOrder,
      })
    );
  };

  const onSubmit = useCallback(() => {
    const submitOrder = async () => {
      if (accountNumber && seedOrders) {
        await dispatch(syncSeedCart({ seedOrder: seedOrders, accountNumber }))
          .unwrap()
          .then((result: unknown) => {
            // No result usually meant there was an error so do not proceed
            if (result) {
              navigate(SEED_SUMMARY_ROUTE);
            }
          })
          .catch((e: Error) => {
            if (e.message === SeedErrorCode.InvalidOrder) {
              toast("Order out of date. Order has been updated", {
                autoClose: 100000,
              });
            }
          });
      }
    };
    submitOrder();
  }, [accountNumber, seedOrders]);

  return {
    // useFormApi,
    copySeed,
    addCustomSeed,
    addPremixSeed,
    onSubmit: onSubmit,
  };
};
